/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {getErrorMessage, throttleQueue} from '@exc/shared';
import {Level} from '@adobe/exc-app/metrics/Level';
import type {Metrics, MetricsApi} from '@adobe/exc-app/metrics';
import type {Poller, PollerHandle, PollingConfiguration, UserActivityMonitor} from '@adobe/exc-app/internal';

export interface PollingService {
  /**
   * Adds a poller config to be polled.
   * @param {Poller} poller - Poller config & polling function.
   */
  addPoller: (poller: Poller) => PollerHandle;
  /**
   * Configure the polling service.
   * @param {PollingConfiguration} config - Polling Service config.
   */
  configure: (config: PollingConfiguration) => void;
  /**
   * Removes a poller.
   * @param {PollerHandle} handle
   */
  removePoller: (handle: PollerHandle) => void;
}

interface PollerState {
  frequencyMs: number;
  intervalHandle?: number;
  lastPoll: number;
  poller: Poller;
}

class PollingServiceImpl implements PollingService {
  private _pollWhileInactive = true;
  private readonly _state: Record<string, PollerState> = {};
  private readonly _activityMonitor: UserActivityMonitor;
  private readonly metrics: Metrics;
  private readonly clearInterval: (state: PollerState) => void;
  private readonly setInterval: (handler: TimerHandler, timeout?: number) => number;

  constructor(activityMonitor: UserActivityMonitor, metricsApi: MetricsApi, win: Window) {
    // Listen to both inactive and hidden if we have 2 different frequencies for inactive/hidden.
    // Active should always be shown, so no need to listen to 'shown'.
    this.metrics = metricsApi.create('exc.module-runtime.network.pollingService');
    this.setInterval = win.setInterval.bind(win);
    this.clearInterval = (state: PollerState) => {
      if (state.intervalHandle) {
        win.clearInterval(state.intervalHandle);
        delete state.intervalHandle;
      }
    };
    const updateFn = () => this.updateAllIntervals();
    const throttledFn = throttleQueue(updateFn, 16, false, win);
    this._activityMonitor = activityMonitor;
    this._activityMonitor.on('inactive', throttledFn);
    this._activityMonitor.on('hidden', throttledFn);
    this._activityMonitor.on('active', updateFn);
  }

  private async poll(state: PollerState): Promise<void> {
    const {poller} = state;
    const timer = this.metrics.start(`Polling.${poller.name}`);
    try {
      await poller.pollFn();
      state.lastPoll = Date.now();
      timer.time('done');
    } catch (err) {
      timer.time('PollFailed', {error: getErrorMessage(err)}, {level: Level.ERROR});
    }
  }

  private updatePollerState(state: PollerState): void {
    const {poller, frequencyMs, lastPoll} = state;
    const {activeFrequency, hiddenFrequency, inactiveFrequency} = poller;
    let newFrequency = activeFrequency;
    if (!this._activityMonitor.active) {
      newFrequency = (!this._activityMonitor.hidden || hiddenFrequency === undefined) ? inactiveFrequency : hiddenFrequency;
      if (!this._pollWhileInactive) {
        // Polling disabled during inactivity.
        newFrequency = 0;
      }
    }
    if (frequencyMs !== newFrequency) {
      // Frequency set or updated.
      this.clearInterval(state);
      state.frequencyMs = newFrequency;
      state.intervalHandle = newFrequency > 0 ? this.setInterval(() => this.poll(state), newFrequency) : 0;
      if (this._activityMonitor.active && newFrequency && (Date.now() - lastPoll > newFrequency)) {
        this.poll(state);
      }
    }
  }

  private updateAllIntervals(): void {
    Object.values(this._state).forEach(state => this.updatePollerState(state));
  }

  public configure({pollWhileInactive}: PollingConfiguration): void {
    if (pollWhileInactive !== undefined) {
      this._pollWhileInactive = pollWhileInactive;
      this.metrics.log('Polling configured', {pollWhileInactive: this._pollWhileInactive});
      this.updateAllIntervals();
    }
  }

  public addPoller(poller: Poller): PollerHandle {
    const handle = `${poller.name}-${Date.now()}`;
    const state = this._state[handle] = {
      frequencyMs: 0,
      intervalHandle: 0,
      lastPoll: Date.now(),
      poller
    };
    this.updatePollerState(state);
    if (poller.immediate) {
      this.poll(state);
    }
    return {handle};
  }

  public removePoller({handle}: PollerHandle): void {
    const state = this._state[handle];
    if (state) {
      this.clearInterval(state);
      delete this._state[handle];
    }
  }
}

export default (activityMonitor: UserActivityMonitor, metrics: MetricsApi, win: Window): PollingService =>
  new PollingServiceImpl(activityMonitor, metrics, win);
